import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { productionDummyData } from './dummyData';
import { useNavigate } from 'react-router-dom';

const STEPS = [
    { key: 'rencana', label: 'Rencana', color: '#3b82f6' },
    { key: 'proses', label: 'Produksi', color: '#f59e0b' },
    { key: 'realisasi', label: 'Realisasi', color: '#22c55e' }
];

const TvMode = () => {
    const [data] = useState(productionDummyData);
    const [currentTime, setCurrentTime] = useState(new Date());
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const totalDefisit = data.filter(d => d.nasional.stok_min_sp < 0).reduce((a, d) => a + Math.abs(d.nasional.stok_min_sp), 0);
    const totalSP = data.reduce((a, d) => a + d.nasional.sp_belum, 0);
    const inProd = data.filter(d => d.status.step === 2).length;

    // ── TV Status Pipeline (bigger)
    const TvPipeline = ({ status }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {STEPS.map((s, i) => {
                const ok = status[s.key]; const active = status.step === i + 1;
                return (<React.Fragment key={s.key}>
                    <div title={s.label} style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: ok ? s.color : active ? `${s.color}40` : '#374151', border: active ? `3px solid ${s.color}` : '2px solid #374151' }}>
                        <i className={`pi ${ok ? 'pi-check' : 'pi-circle'}`} style={{ fontSize: 14, color: ok ? '#fff' : active ? s.color : '#6b7280' }}></i>
                    </div>
                    {i < 2 && <div style={{ width: 16, height: 3, background: ok && status[STEPS[i + 1]?.key] ? STEPS[i + 1].color : '#374151', borderRadius: 2 }}></div>}
                </React.Fragment>);
            })}
        </div>
    );

    // ── TV Progress bar
    const TvProgress = ({ d }) => {
        if (d.rencana_qty === 0) return <span style={{ color: '#6b7280', fontSize: 20 }}>-</span>;
        return (
            <div style={{ minWidth: 140 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: '#d1d5db', marginBottom: 4 }}>
                    <span>{d.realisasi_qty.toLocaleString()}/{d.rencana_qty.toLocaleString()}</span>
                    <span style={{ color: d.progress === 100 ? '#22c55e' : '#f59e0b' }}>{d.progress}%</span>
                </div>
                <div style={{ width: '100%', height: 8, background: '#374151', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${d.progress}%`, height: '100%', background: d.progress === 100 ? '#22c55e' : d.progress > 50 ? '#3b82f6' : '#f59e0b', borderRadius: 4, transition: 'width 1s ease' }}></div>
                </div>
            </div>
        );
    };

    // ── Deficit for TV
    const tvDeficit = (v) => {
        if (v < 0) return <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '8px 16px', borderRadius: 10, textAlign: 'center', fontWeight: 800, fontSize: 22, animation: 'pulse 2s infinite' }}>-{Math.abs(v).toLocaleString()}</div>;
        return <div style={{ background: '#14532d', color: '#86efac', padding: '8px 16px', borderRadius: 10, textAlign: 'center', fontWeight: 800, fontSize: 22 }}>+{v.toLocaleString()}</div>;
    };

    const prioBadge = (level) => {
        const cfg = { URGENT: { bg: '#dc2626', t: '#fff' }, HIGH: { bg: '#f59e0b', t: '#fff' }, NORMAL: { bg: '#374151', t: '#9ca3af' } };
        const c = cfg[level] || cfg.NORMAL;
        return <span style={{ background: c.bg, color: c.t, fontWeight: 800, fontSize: 16, padding: '4px 12px', borderRadius: 6 }}>{level}</span>;
    };

    return (
        <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column', padding: 20 }}>

            {/* ═══ Header ═══ */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', padding: '16px 24px', borderRadius: 16, marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,.3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 24 }}><i className="pi pi-arrow-left"></i></button>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <i className="pi pi-chart-pie" style={{ color: '#818cf8' }}></i> LAPORAN STOK HARIAN SUMA
                        </h1>
                        <p style={{ margin: '4px 0 0', fontSize: 16, color: '#94a3b8' }}>
                            {currentTime.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 48, fontWeight: 800, color: '#22d3ee', fontFamily: 'monospace', lineHeight: 1 }}>
                        {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                </div>
            </div>

            {/* ═══ KPI Strip ═══ */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                {[
                    { label: 'TOTAL DEFISIT', value: totalDefisit.toLocaleString(), color: '#ef4444', icon: 'pi-exclamation-triangle' },
                    { label: 'SP BELUM KIRIM', value: totalSP.toLocaleString(), color: '#3b82f6', icon: 'pi-shopping-bag' },
                    { label: 'DALAM PRODUKSI', value: `${inProd} SKU`, color: '#f59e0b', icon: 'pi-cog' },
                ].map((k, i) => (
                    <div key={i} style={{ flex: 1, background: '#1e293b', borderRadius: 12, padding: '14px 20px', borderLeft: `5px solid ${k.color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>{k.label}</div>
                            <div style={{ fontSize: 32, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</div>
                        </div>
                        <i className={`pi ${k.icon}`} style={{ fontSize: 32, color: `${k.color}60` }}></i>
                    </div>
                ))}
            </div>

            {/* ═══ Main Table ═══ */}
            <div style={{ flex: 1, background: '#1e293b', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,.3)' }}>
                <DataTable value={data} className="tv-table" scrollable scrollHeight="calc(100vh - 280px)" sortField="nasional.stok_min_sp" sortOrder={1} rowHover>
                    <Column header="KODE" body={d => <span style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>{d.kode}</span>} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                    <Column header="NAMA BARANG" body={d => <span style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>{d.nama}</span>} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                    <Column header="PRIORITAS" body={d => prioBadge(d.prioritas)} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                    <Column header="PIPELINE" body={d => <TvPipeline status={d.status} />} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                    <Column header="PROGRESS" body={d => <TvProgress d={d} />} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} style={{ minWidth: 180 }} />
                    <Column header="STOK PUSAT" body={d => <span style={{ fontSize: 22, fontWeight: 700, color: '#d1d5db' }}>{d.stok_pusat.toLocaleString()}</span>} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                    <Column header="SP BELUM KIRIM" body={d => <span style={{ fontSize: 22, fontWeight: 800, color: '#fbbf24' }}>{d.nasional.sp_belum.toLocaleString()}</span>} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                    <Column header="STOK - SP (NASIONAL)" body={d => tvDeficit(d.nasional.stok_min_sp)} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} style={{ minWidth: 200 }} />
                    <Column header="PIC" body={d => <span style={{ fontSize: 18, fontWeight: 600, color: '#a5b4fc' }}>{d.pic}</span>} headerStyle={{ background: '#0f172a', color: '#94a3b8', fontSize: 16, fontWeight: 700, padding: '14px 16px', borderBottom: '2px solid #334155' }} />
                </DataTable>
            </div>

            <style jsx="true">{`
                .tv-table .p-datatable-tbody > tr > td {
                    padding: 16px !important;
                    background: transparent !important;
                    border-bottom: 1px solid #1e293b;
                }
                .tv-table .p-datatable-tbody > tr:hover > td {
                    background: #334155 !important;
                }
                .tv-table .p-datatable-tbody > tr:nth-child(even) > td {
                    background: #0f172a40 !important;
                }
                @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.6; } }
            `}</style>
        </div>
    );
};

export default TvMode;
